import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "./baseQuery";
import {
  Response,
  UrlResponse,
  User,
  UserInfo,
  ChangeUserPassword,
  ListResponse,
  UserThatShareCampaign,
} from "types/apiTypes";
import { RequestMethods } from "types/enums";
import { Query } from "../types";
import { prepareQuery } from "../utils/prepareQuery";

export enum UserTags {
  user = "user",
  internal_users = "internal-users",
}

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(UserTags),
  endpoints: (builder) => ({
    getUser: builder.query<User, void>({
      query: () => ({
        url: "/me/profile",
        method: RequestMethods.GET,
      }),
      providesTags: [UserTags.user],
    }),

    changeUserInfo: builder.mutation<Response, UserInfo>({
      query: (body) => ({
        url: "/me/profile",
        method: RequestMethods.PATCH,
        body,
      }),
    }),

    changeUserPassword: builder.mutation<Response, ChangeUserPassword>({
      query: (body) => ({
        url: "/me/password",
        method: RequestMethods.PATCH,
        body,
      }),
    }),

    changeUserAvatar: builder.mutation<UrlResponse, FormData>({
      query: (body) => ({
        url: "/me/avatar",
        method: RequestMethods.POST,
        body,
      }),
    }),

    removeUserAvatar: builder.mutation<Response, void>({
      query: () => ({
        url: "/me/avatar",
        method: RequestMethods.DELETE,
      }),
    }),

    updateClientView: builder.mutation<Response, boolean>({
      query: (client_view) => ({
        url: "/me/client-view",
        method: RequestMethods.PATCH,
        body: { client_view },
      }),
    }),

    updateFirstLogin: builder.mutation<Response, void>({
      query: () => ({
        url: "/me/first-login",
        method: RequestMethods.PATCH,
      }),
    }),

    getUserByEmail: builder.query<UserThatShareCampaign, string>({
      query: (email) => ({
        url: `users/search?email=${email}`,
        method: RequestMethods.GET,
      }),
    }),

    getInternalUsers: builder.query<ListResponse<UserThatShareCampaign>, Query>(
      {
        query: (params) => ({
          url: `/role-permissions/users${prepareQuery(params)}`,
          method: RequestMethods.GET,
        }),
        keepUnusedDataFor: 0,
        providesTags: [UserTags.internal_users],
      },
    ),

    updateUserPermissions: builder.mutation<Response, UserThatShareCampaign>({
      query: ({ user_id, permissions }) => ({
        url: `/role-permissions/users/${user_id}`,
        method: RequestMethods.PATCH,
        body: { permissions },
      }),
      invalidatesTags: [UserTags.internal_users],
    }),
  }),
});

export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useChangeUserInfoMutation,
  useChangeUserPasswordMutation,
  useChangeUserAvatarMutation,
  useRemoveUserAvatarMutation,
  useUpdateClientViewMutation,
  useUpdateFirstLoginMutation,
  useLazyGetUserByEmailQuery,
  useGetInternalUsersQuery,
  useUpdateUserPermissionsMutation,
} = userApi;
